import React, { useCallback, useEffect, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import styles from './dateExpiredChecker.module.scss'
import WarningIcon from '../Icons/./WarningIcon'
import { diffDays } from '../../helpers/itemsGroupHelper'
import { stringToISO } from '../../helpers/timeHelper'
import { StyledTooltip } from '../Tooltip/Tooltip.config'
import { requestOpen, release } from './expireTooltipSingleton'

const DateExpiredChecker = ({ children }) => {
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)')
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const handleTooltipClose = useCallback(() => {
    release(handleTooltipClose)
    setIsTooltipOpen(false)
  }, [])

  const handleTooltipToggle = useCallback(() => {
    if (canHover) return
    if (isTooltipOpen) {
      handleTooltipClose()
    } else {
      requestOpen(handleTooltipClose)
      setIsTooltipOpen(true)
    }
  }, [canHover, isTooltipOpen, handleTooltipClose])

  useEffect(() => {
    if (canHover) return
    if (!isTooltipOpen) return

    const closeOnScroll = () => handleTooltipClose()
    document.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('wheel', closeOnScroll, { passive: true })
    document.addEventListener('touchmove', closeOnScroll, { passive: true })
    document.addEventListener('pointermove', closeOnScroll, { passive: true })

    return () => {
      document.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('wheel', closeOnScroll)
      document.removeEventListener('touchmove', closeOnScroll)
      document.removeEventListener('pointermove', closeOnScroll)
    }
  }, [canHover, isTooltipOpen, handleTooltipClose])

  if (!children) return 'current'

  const currentDate = new Date()
  const isoDate = stringToISO(children)
  const diff = diffDays(new Date(isoDate), currentDate)

  const result = {}

  if (new Date(isoDate).getTime() < currentDate.getTime()) {
    result.warning = 'Document expired'
    result.circleFill = '#EB5757'
  } else if (diff <= 30) {
    result.warning = `Document expires in ${diff} days`
    result.circleFill = '#E7B92A'
  } else {
    result.warning = ''
    result.circleFill = 'inherit'
    result.pathFill = 'inherit'
  }

  const tooltipProps = canHover
    ? { arrow: true, placement: 'top' }
    : {
        arrow: true,
        placement: 'top',
        open: isTooltipOpen,
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
        onClose: handleTooltipClose,
      }

  return (
    <div className={styles.wrapper}>
      <span>{children}</span>

      {result.warning ? (
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <StyledTooltip title={result.warning} {...tooltipProps}>
            <span
              className={styles.expireTooltip}
              onClick={handleTooltipToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleTooltipToggle()
              }}
              role="button"
              tabIndex={canHover ? -1 : 0}
              aria-label={result.warning}
            >
              <WarningIcon circleFill={result.circleFill} pathFill={result.pathFill} />
            </span>
          </StyledTooltip>
        </ClickAwayListener>
      ) : (
        <span className={styles.expireTooltip}>
          <WarningIcon circleFill={result.circleFill} pathFill={result.pathFill} />
        </span>
      )}
    </div>
  )
}

export default DateExpiredChecker
